import {
  GENERATE_AD_URL,
  GENERATE_AD_V3_URL,
  GENERATE_AUTH_TOKEN,
  USE_ATLAS_BACKEND,
} from '../config';
import { atlasBackendFetch } from './atlasBackend';

export type GenerateAdErrorType =
  | 'policy_violation'
  | 'model_refusal'
  | 'billing'
  | 'provider_timeout'
  | 'technical'
  /** 400 del backend nuevo: el pedido no puede salir así (un producto sin fotos, la marca
   *  de otro rubro…). El `message` del backend ya dice qué tocar y se muestra tal cual. */
  | 'validation';

// Copy NEUTRO (sirve para imagen, video y audio) y con la aclaración de créditos:
// el débito es al ENTREGAR (ADR-124 del backend), así que ningún fallo consume.
// Mismo tono que userMessages.ts del backend — si cambia uno, cambiar el otro.
const ERROR_COPY: Record<GenerateAdErrorType, string> = {
  policy_violation:
    'El contenido fue rechazado por las políticas de moderación de la IA. Puede deberse a contenido sensible, marcas o personas reconocibles — ajustalo e intentá de nuevo. No se consumieron créditos.',
  model_refusal:
    'La IA no pudo completar esta generación. Probá reformulando el pedido con otras palabras. No se consumieron créditos.',
  billing:
    'No te alcanzan los créditos para esta generación. Sumá créditos o actualizá tu plan para continuar.',
  provider_timeout:
    'La generación tardó más de lo esperado y se canceló. Intentá de nuevo en unos minutos. No se consumieron créditos.',
  technical:
    'Tuvimos un problema técnico con esta generación. Intentá de nuevo en un momento. No se consumieron créditos.',
  validation:
    'Hay un problema con los datos del pedido. Revisalo e intentá de nuevo. No se consumieron créditos.',
};

export function mapErrorTypeToMessage(errorType: GenerateAdErrorType): string {
  return ERROR_COPY[errorType] ?? ERROR_COPY.technical;
}

export class GenerateAdError extends Error {
  public readonly errorType: GenerateAdErrorType;
  public readonly raw: string | null;

  constructor(errorType: GenerateAdErrorType, message: string, raw: string | null = null) {
    super(message);
    this.name = 'GenerateAdError';
    this.errorType = errorType;
    this.raw = raw;
  }

  // Errores donde reintentar con el mismo prompt no va a funcionar — el usuario
  // tiene que cambiar el pedido (o su plan, en `billing`). Para estos NO
  // mostramos "intentá de nuevo" ni el flujo de "contactá soporte" — el
  // problema no es nuestro. `billing` tiene que ser accionable además para que
  // `awaitImageGeneration` corte al toque en vez de esperar el poll 5 min.
  get isActionable(): boolean {
    return (
      this.errorType === 'policy_violation' ||
      this.errorType === 'model_refusal' ||
      this.errorType === 'billing' ||
      this.errorType === 'validation'
    );
  }
}

export interface GenerateAdResponse {
  response?: string;
  error_type?: GenerateAdErrorType | null;
  error_message?: string | null;
  imagenes_generadas_ids?: Array<string | number>;
  id_company?: string;
  [key: string]: unknown;
}

// Parseo compartido de una respuesta RESUELTA (HTTP 200, shape idéntica entre
// n8n y el backend nuevo): error tipado → GenerateAdError; éxito sin ids →
// unexpected_response; éxito con ids → data.
function parseResolvedResponse(data: GenerateAdResponse): GenerateAdResponse {
  // Error tipado del backend (workflow Generar anuncio V2 / atlas-backend)
  if (data?.response === 'error' && data?.error_type) {
    const t = (data.error_type as GenerateAdErrorType) || 'technical';
    throw new GenerateAdError(
      t,
      mapErrorTypeToMessage(t),
      data.error_message ?? null,
    );
  }

  // Validar respuesta exitosa esperada
  if (
    data?.response !== 'success' ||
    !Array.isArray(data?.imagenes_generadas_ids) ||
    data.imagenes_generadas_ids.length === 0
  ) {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      'unexpected_response',
    );
  }

  return data;
}

// ─── Ruteo al backend nuevo (migración n8n → atlas-backend) ────────────────
//
// Migran las generaciones de la Interfaz V2 (endpoint V3) en modos `rapido`,
// `personalizado`, `anuncio`, `anuncio_desde_imagen` (ADR-577) y `plantilla`
// (ADR-580 — multi-plantilla entrega una imagen por template; el backend
// además resuelve el placeholder {{model}} que en n8n viajaba literal), y el
// funnel clásico completo contra el V2 (ADR-596). Con
// ADR-2026-08-23-migrar-modelo-mode-multiple también migró `modelo_mode:
// 'multiple'` (composición grupal): ya no queda ningún camino de imagen en n8n.
//
// Ruteo por (endpoint, modo): el V3 fue lo primero migrado (ADR-570) y desde
// ADR-596 el funnel clásico (webhook V2 `generar_anuncio_v2`) también rutea —
// el backend soporta sus modos, `rapido` ya no necesita `new_ui` y "sin
// créditos" responde al toque en vez de colgar 300s. Los bodies del V2 no
// traen `execution_id`/`modo_utilizado`: los inyecta `postGenerateAd` SOLO en
// el path atlas (el body original no se toca — flag OFF byte-idéntico).
// Exportada: InterfazV2.handleGenerateCatalog la usa para decidir el AGRUPADO
// de la tanda (atlas fusiona pose[] + poses_custom[] en un request; n8n exige
// fan-out porque solo lee poses_custom[0]). Ver ADR-576.

// Modos del funnel clásico (V2) que el atlas ya soporta (ADR-596).
const MODOS_V2_MIGRADOS = new Set([
  'rapido',
  'personalizado',
  'creativo',
  'creativo_plus',
  'plantilla',
  'start_video_ugc',
  'start_video_personalizado',
]);

export function shouldRouteToAtlas(
  body: Record<string, unknown>,
  endpoint: string,
): boolean {
  // Modelo: ALLOWLIST a propósito. El backend implementa modelo_mode 'ia',
  // 'guardados' y 'personalizado' (modelo custom con paridad V3: genero,
  // edad, tipo_cuerpo, tono_piel, color_pelo, largo_pelo, desc_modelo —
  // deploy 2026-07-20, ver ADR-572/575) y 'multiple' (composición grupal:
  // un individual por slot de modelo_config sin cobro + la composición
  // entregada — ADR-2026-08-23-migrar-modelo-mode-multiple; requiere el
  // backend deployado con esa rama, si no responde 400 validation).
  const mm = body.modelo_mode;
  const modeloSoportado =
    mm === undefined ||
    mm === 'ia' ||
    mm === 'guardados' ||
    mm === 'personalizado' ||
    mm === 'multiple';
  const modoMigradoV3 =
    body.modo === 'rapido' ||
    body.modo === 'personalizado' ||
    body.modo === 'anuncio' ||
    body.modo === 'anuncio_desde_imagen' ||
    body.modo === 'plantilla';
  const rutaMigrada =
    (endpoint === GENERATE_AD_V3_URL && modoMigradoV3) ||
    (endpoint === GENERATE_AD_URL && MODOS_V2_MIGRADOS.has(String(body.modo)));
  return USE_ATLAS_BACKEND && rutaMigrada && modeloSoportado;
}

// Etiqueta de analytics para el panel cuando el funnel clásico rutea al atlas
// y la pantalla no mandó `modo_utilizado` propio. Los valores de imagen
// replican los que el historial ya conocía del V2 de n8n.
const MODO_UTILIZADO_CLASICO: Record<string, string> = {
  rapido: 'imagen_automatica',
  personalizado: 'imagen_personalizada',
  creativo: 'creativo',
  creativo_plus: 'creativo_plus',
  plantilla: 'plantilla',
  start_video_ugc: 'video_ugc',
  start_video_personalizado: 'video_personalizado',
};

// POST {ATLAS_BACKEND_URL}/v1/ads/generate — mismo body que n8n pero SIN
// `user_id` (el backend lo deriva del JWT). EXPORTADA además para
// `generateFreeformImage` del asistente (modo `creativo_plus`, ADR-583):
// ese flujo NO puede rutear por `postGenerateAd` porque su fallback n8n es
// el webhook V2 (`generar_anuncio_v2`), no el V3 — el flag lo maneja el
// propio agentGenerate llamando esta función directo.
// Diferencias de respuesta vs n8n:
//  • HTTP 200 → misma shape resuelta ({response, error_type, ...ids}).
//  • HTTP 202 {status:'pending', job_id} → el wait del backend venció (>150s).
//    NO es error: devolvemos ids vacíos y el poll por `execution_id`
//    (awaitImageGeneration) termina el trabajo — el backend sigue escribiendo
//    una fila por imagen en `imagenes_generadas` con ese execution_id.
//  • HTTP 402 {error_type:'billing'} → sin créditos (error accionable).
//  • HTTP 400 {error_type:'validation'} → error técnico + log: indica un modo
//    que NO correspondía rutear acá.
export async function postGenerateAdAtlas(
  body: Record<string, unknown>,
): Promise<GenerateAdResponse> {
  const { user_id: _userId, ...atlasBody } = body;
  void _userId;

  let res: Response;
  try {
    res = await atlasBackendFetch('/v1/ads/generate', atlasBody);
  } catch (e: any) {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      e?.message ?? 'network_error',
    );
  }

  let data: GenerateAdResponse | null = null;
  try {
    data = (await res.json()) as GenerateAdResponse;
  } catch {
    data = null;
  }

  if (res.status === 202) {
    // Timeout del wait del backend — la generación sigue corriendo. El caller
    // ya tolera respuesta sin ids (gana el poll por execution_id).
    return {
      response: 'pending',
      status: 'pending',
      job_id: (data as any)?.job_id,
      imagenes_generadas_ids: [],
    };
  }

  if (res.status === 402) {
    throw new GenerateAdError(
      'billing',
      mapErrorTypeToMessage('billing'),
      data?.error_message ?? 'billing',
    );
  }

  if (res.status === 400 && data?.error_type === ('validation' as string)) {
    console.error(
      '[postGenerateAd] validation error del backend nuevo — este modo no debería rutearse acá:',
      data,
    );
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      data?.error_message ?? 'validation_error',
    );
  }

  if (!res.ok) {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      data ? JSON.stringify(data) : `HTTP ${res.status}`,
    );
  }

  if (data == null) {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      'invalid_json',
    );
  }

  return parseResolvedResponse(data);
}

/**
 * Dispara la generación de imagen. Según el flag `USE_ATLAS_BACKEND` y el
 * (endpoint, modo) del request, rutea al backend nuevo de Atlas
 * (`/v1/ads/generate`, solo V3 rapido/personalizado) o al webhook de n8n
 * como siempre. Devuelve el JSON del response cuando hay éxito; si algo
 * falla, lanza `GenerateAdError` con el `errorType` ya clasificado
 * (policy_violation, model_refusal, billing o technical).
 *
 * Los callers deciden cómo mostrar el mensaje:
 *  - Si `err.isActionable` → usar `err.message` directo (sin "intentá de nuevo").
 *  - Si no → mantener su lógica actual de reintentos / "contactanos".
 *
 * Caso especial backend nuevo: puede resolver con
 * `{response:'pending', imagenes_generadas_ids: []}` (HTTP 202) — la
 * generación sigue corriendo y la termina el poll por `execution_id`.
 */
export async function postGenerateAd(
  body: Record<string, unknown>,
  opts?: { url?: string },
): Promise<GenerateAdResponse> {
  const endpoint = opts?.url || GENERATE_AD_URL;

  if (shouldRouteToAtlas(body, endpoint)) {
    if (endpoint === GENERATE_AD_URL) {
      // Funnel clásico: el atlas exige `execution_id` (estas pantallas no lo
      // mandan) y `modo_utilizado` etiqueta la pantalla en el panel. Van en
      // una COPIA solo para el path atlas: el body original sigue viajando a
      // n8n intacto con flag OFF, y `lastPayload`/"Generar más" no arrastran
      // un execution_id repetido.
      const atlasBody: Record<string, unknown> = { ...body };
      if (!atlasBody.execution_id) {
        atlasBody.execution_id = crypto.randomUUID();
      }
      if (!atlasBody.modo_utilizado) {
        const etiqueta = MODO_UTILIZADO_CLASICO[String(body.modo)];
        if (etiqueta) atlasBody.modo_utilizado = etiqueta;
      }
      return await postGenerateAdAtlas(atlasBody);
    }
    return await postGenerateAdAtlas(body);
  }

  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify(body),
    });
  } catch (e: any) {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      e?.message ?? 'network_error',
    );
  }

  if (!res.ok) {
    let text: string | null = null;
    try {
      text = await res.clone().text();
    } catch {}
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      text || `HTTP ${res.status}`,
    );
  }

  let data: GenerateAdResponse;
  try {
    data = (await res.json()) as GenerateAdResponse;
  } catch {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      'invalid_json',
    );
  }

  return parseResolvedResponse(data);
}
